import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import SoftAvatar from "components/SoftAvatar";
import userImage from "assets/images/illustrations/user.png";
import SoftButton from "components/SoftButton";
import { ApiResponse } from "model/apiResponseModel";
import api from "api/api";
import { useNavigate } from "react-router-dom";
import SoftBadge from "components/SoftBadge";
import MilestoneFilter from "./components/MilestoneFilter";
import ReviewDialog from "./components/ReviewDialog";

export default function MilestoneReviewRequests() {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;
  const navigate = useNavigate();
  const [status, setStatus] = useState("pending");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingAction, setSubmittingAction] = useState(null);

  const fetchRequests = async (page = 1) => {
    try {
      setIsLoading(true);
      let postData = {
        limit,
        page,
        status,
      };

      const response = await api.getReviewMilstoneRequest(postData);
      console.log(response);
      const apiResponse = new ApiResponse(response.status, response.message);

      if (apiResponse.status === 1) {
        setColumns([
          { name: "user", align: "left" },
          { name: "project design", align: "center" },
          { name: "artist", align: "center" },
          { name: "brand", align: "center" },
          { name: "ai claimed", align: "center" },
          { name: "user requested", align: "center" },
          { name: "created", align: "center" },
          { name: "status", align: "center" },
          { name: "action", align: "center" },
        ]);

        setRows(
          response.data.requests.map((item) => ({
            user: (
              <SoftBox display="flex" alignItems="center" px={1} pb={0.5}>
                <SoftAvatar
                  src={userImage}
                  alt={item.user_name}
                  size="sm"
                  variant="rounded"
                />
                <SoftBox display="flex" flexDirection="column" ml={2}>
                  <SoftTypography variant="button" fontWeight="medium">
                    {item.user_name}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="secondary">
                    {item.user_email}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            ),
            "project design": (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {item.project_design}
              </SoftTypography>
            ),
            artist: (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {item.project_artist}
              </SoftTypography>
            ),
            brand: (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {item.brand}
              </SoftTypography>
            ),
            "ai claimed": (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {item.ai_verified_percent}%
              </SoftTypography>
            ),
            "user requested": (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {item.manual_user_percentage}%
              </SoftTypography>
            ),
            created: (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {item.created_at}
              </SoftTypography>
            ),
            status: (
              <SoftBadge
                variant="gradient"
                badgeContent={
                  item.status === "pending"
                    ? "Pending"
                    : item.status === "approved"
                    ? "Approved"
                    : item.status === "rejected"
                    ? "Rejected"
                    : item.status
                }
                color={
                  item.status === "pending"
                    ? "warning"
                    : item.status === "approved"
                    ? "success"
                    : item.status === "rejected"
                    ? "error"
                    : "info"
                }
                size="xs"
              />
            ),
            action: (
              <SoftButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => handleReviewClick(item)}
                disabled={item.status !== "pending"}
              >
                Review
              </SoftButton>
            ),
          }))
        );
        setTotalPages(Math.ceil(response.data.total / limit));
      }
    } catch (error) {
      console.error("Error fetching milestone requests:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loginData = JSON.parse(
      localStorage.getItem("loginData") || sessionStorage.getItem("loginData")
    );

    if (!loginData) {
      navigate("/authentication/sign-in");
    } else {
      fetchRequests(currentPage);
    }
  }, [currentPage, navigate, status]);

  const handlePageClick = (page) => {
    setCurrentPage(page);
    fetchRequests(page);
  };

  const handleReviewClick = (request) => {
    setSelectedRequest(request);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (isSubmitting) return;
    setDialogOpen(false);
    setSelectedRequest(null);
  };

  const handleReviewSubmit = async (reviewData) => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setSubmittingAction(reviewData.status);
      const response = await api.reviewMilestoneRequest(reviewData);
      if (Number(response?.status) === 1) {
        setDialogOpen(false);
        setSelectedRequest(null);
        fetchRequests(currentPage);
        return;
      }
      alert(response?.message || "Failed to process review request");
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || error?.message || "Failed to process review request";
      alert(errorMessage);
      console.error("Error reviewing milestone request:", error);
    } finally {
      setIsSubmitting(false);
      setSubmittingAction(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
              <SoftTypography variant="h6" width="30%">
                Milestone Review Requests
              </SoftTypography>
              <MilestoneFilter status={status} setStatus={setStatus} />
            </SoftBox>
            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {isLoading ? (
                <SoftBox
                  display="flex"
                  height="500px"
                  justifyContent="center"
                  alignItems="center"
                  p={3}
                >
                  <SoftTypography variant="subtitle1" color="textSecondary">
                    Loading...
                  </SoftTypography>
                </SoftBox>
              ) : rows.length > 0 ? (
                <Table columns={columns} rows={rows} />
              ) : (
                <SoftBox display="flex" justifyContent="center" alignItems="center" p={3}>
                  <SoftTypography variant="subtitle1" color="textSecondary">
                    No Requests Found
                  </SoftTypography>
                </SoftBox>
              )}
            </SoftBox>
            <SoftBox display="flex" alignItems="center" justifyContent="center" py={2}>
              {totalPages > 1 && (
                <SoftButton
                  variant="outlined"
                  color="primary"
                  onClick={() => handlePageClick(currentPage - 1)}
                  sx={{ mx: 1 }}
                >
                  Previous
                </SoftButton>
              )}

              <SoftButton
                key={currentPage}
                variant="contained"
                color="primary"
                sx={{ mx: 0.5 }}
              >
                {currentPage}
              </SoftButton>

              {totalPages > 1 && (
                <SoftButton
                  variant="outlined"
                  color="primary"
                  onClick={() => handlePageClick(currentPage + 1)}
                  sx={{ mx: 1 }}
                >
                  Next
                </SoftButton>
              )}
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>
      <Footer />
      {selectedRequest && (
        <ReviewDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          request={selectedRequest}
          onSubmit={handleReviewSubmit}
          isSubmitting={isSubmitting}
          submittingAction={submittingAction}
        />
      )}
    </DashboardLayout>
  );
}
